import {
  CurrentUserFragment,
  ListingObject,
  MlsListingAddressObject,
  MlsListingFragment,
  MlsListingSearchResultFragment,
  MlsListingStatus,
  PublicListingFragment,
} from '@client/graphql/__generated__/types';
import dayjs from 'dayjs';
import { encode } from 'ngeohash';
import { getMediaUrl } from '~/common/url';
import {
  DEFAULT_DATETIME_FORMAT,
  DEFAULT_DATE_FORMAT,
} from '~/common/utils/formatter';
import { MlsListingWithListing } from '../components/MapSearch/ExploreContext';
import { StyledFormInputType } from '../components/StyledFormInput/StyledFormInput';
import {
  PREFERENCE_VALUE_FIELD_MAP,
  getPreferenceMetaBySlug,
} from '../pages/ListingSetup/Preferences/constants';

interface MlsListingForSqFtValue {
  listPrice?: number;
  property?: {
    area?: number;
  };
}

export const DEFAULT_PROPERTY_IMAGE_URL = getMediaUrl(
  'medias/default-house.svg'
);

export const getPropertySource = (src?: string) =>
  src || DEFAULT_PROPERTY_IMAGE_URL;

export function getPerSqFtValue(listing?: MlsListingForSqFtValue) {
  const listPrice = listing?.listPrice;
  const area = listing?.property?.area;

  return area && listPrice ? listPrice / area : undefined;
}

/**
 * Get the date or datetime string for a specific preference in the default date or datetime format
 */
export function getPreferenceDateString(
  listing: {
    listingPreferences: {
      preference: { slug: string };
      dateTimeValue?: string;
    }[];
  },
  prefSlug: string,
  format: string = DEFAULT_DATETIME_FORMAT
) {
  const prefVal = listing.listingPreferences.find(
    (prefVal) => prefVal.preference.slug === prefSlug
  );
  const datetimeVal = prefVal?.dateTimeValue;
  const prefDate: Date | undefined = datetimeVal
    ? new Date(datetimeVal)
    : undefined;

  return prefDate ? getFormattedDate(prefDate, format) : undefined;
}

export function getFormattedDateForPreferenceValue(
  prefValue: string,
  prefSlug: string
): string | undefined {
  const preferenceMeta = getPreferenceMetaBySlug(prefSlug);
  if (preferenceMeta?.dataType !== PREFERENCE_VALUE_FIELD_MAP.DATE) {
    return undefined;
  }
  const format =
    preferenceMeta.inputType === StyledFormInputType.DATETIME
      ? DEFAULT_DATETIME_FORMAT
      : DEFAULT_DATE_FORMAT;

  return getFormattedDate(new Date(prefValue), format);
}

// Format date to e.g. "03/12/2024"
export function getFormattedDate(date?: Date, format = DEFAULT_DATE_FORMAT) {
  return date ? dayjs(date).format(format) : undefined;
}

function getFormattedStatusText(statusText?: string): string {
  if (!statusText) return '';

  return statusText.replace(/-/g, ' - ').replace(/\s+/g, ' ').trim();
}

export function getStreetAddressFromMlsListing(
  mlsListingAddress?: MlsListingAddressObject
) {
  if (!mlsListingAddress) {
    return '';
  }

  return `${
    mlsListingAddress?.streetNumberText
      ? `${mlsListingAddress?.streetNumberText} `
      : ''
  }${mlsListingAddress?.streetName}${
    mlsListingAddress?.unit ? ` ${mlsListingAddress?.unit}` : ''
  }`;
}

// Gets listPrice or closePrice based on the status of the listing
export function getListingPrice(
  mlsListing?: MlsListingFragment | MlsListingSearchResultFragment
) {
  if (mlsListing?.mls?.status === MlsListingStatus.Closed) {
    return mlsListing?.sales?.closePrice || mlsListing?.listPrice || 0;
  }

  return mlsListing?.listPrice || 0;
}

export function getIsUserListing(
  listing?: Partial<ListingObject> | Partial<PublicListingFragment>,
  user?: CurrentUserFragment
) {
  if (!listing || !user) {
    return false;
  }

  return (
    listing.listingAgent?.user?.id === user.id ||
    listing.coListingAgent?.user?.id === user.id
  );
}

/**
 * Determines if a listing should be shown as off-market based on deletion status.
 * A listing is off-market if:
 * 1. The listing has been deleted from RETS feed (deletedFromRetsFeedAt is set), OR
 * 2. The MLS listing has been deleted (mlsListing.deleted_at is set)
 */
export function isListingOffMarket(
  listing:
    | Pick<ListingObject, 'mlsListing' | 'deletedFromRetsFeedAt'>
    | Pick<PublicListingFragment, 'mlsListing' | 'deletedFromRetsFeedAt'>
): boolean {
  // Check if listing itself is marked as deleted from RETS feed - if for some reason we don't have
  // an associated MLS listing.
  const listingDeleted = !!listing.deletedFromRetsFeedAt;

  // Check if MLS listing is deleted
  const mlsListingDeleted = !!listing.mlsListing?.deleted_at;

  return listingDeleted || mlsListingDeleted;
}

/**
 * Gets the display status text for a listing, showing "Off Market" for deleted listings
 * or the formatted MLS status text for active listings.
 */
export function getListingDisplayStatus(
  listing:
    | Pick<ListingObject, 'mlsListing' | 'deletedFromRetsFeedAt'>
    | Pick<PublicListingFragment, 'mlsListing' | 'deletedFromRetsFeedAt'>
): string {
  // If listing is off-market, always show "Off Market"
  if (isListingOffMarket(listing)) {
    return 'Off Market';
  }

  // Otherwise, show the formatted MLS status text
  return getFormattedStatusText(listing.mlsListing?.mls?.statusText);
}

export const GEO_HASH_PRECISION = 8; // City blocks

export function groupListingsByGeo(
  listings: MlsListingWithListing[]
): Record<string, MlsListingWithListing[]> {
  const groupedListings = listings.reduce(
    (acc, listing) => {
      const geo = listing.mlsListing.geo;
      if (!geo) {
        return acc;
      }

      const hashKey = encode(
        geo.location.lat,
        geo.location.lon,
        GEO_HASH_PRECISION
      );

      if (!acc[hashKey]) {
        acc[hashKey] = [];
      }

      acc[hashKey].push(listing);

      return acc;
    },
    {} as Record<string, MlsListingWithListing[]>
  );

  return groupedListings;
}
